import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { StepByStepFlowStepId } from '@rio/types'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors, actions, SectionNames } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { BACK_BUTTON_CONDITION_WITH_SCORE_THRESHOLD, LOCALIZATION_PREFIX } from '../constants'
import { constructSectionsData } from '../constructSectionsData'
import { useStepByStepFlowABTest } from './useStepByStepFlowABTest'

export const useStepByStepFlow = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const resume = useTypedSelector(selectors.resume)
  const currentStep = useTypedSelector(selectors.stepByStepFlowCurrentStep)
  const { shouldRedirectToAdditionalStep } = useTypedSelector(selectors.stepByStepFlow)
  const { currentStepEventName } = useStepByStepFlowABTest()
  const resumeScore = resume?.resumeScore || 0
  const { activeSections: sections } = resume
    ? constructSectionsData(resume)
    : { activeSections: [] }

  const currentIndex = useMemo(() => {
    if (!sections) return -1
    return sections.findIndex(
      item =>
        item.id === currentStep ||
        item.sortId === currentStep ||
        item.sortId === `custom:${currentStep}`,
    )
  }, [currentStep, sections])

  const handleChangeStep = useCallback(
    (sectionId: StepByStepFlowStepId) => {
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: true }))
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: sectionId,
            },
          },
          debounce: true,
        }),
      )
    },
    [dispatch],
  )

  const handleBack = useCallback(() => {
    if (!sections) return
    const previousStep = sections[currentIndex + -1]?.id
    dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    if (currentIndex > 0) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: previousStep ?? currentStep,
            },
          },
          debounce: false,
        }),
      )
      if (currentStep) {
        trackInternalEvent('click_back', {
          label: 'step_builder',
          step: currentStepEventName,
          resume_id: resume?.id,
        })
      }
    } else {
      dispatch(
        actions.updateSimpleField({
          name: 'showPrefillResumeModal',
          value: true,
          debounce: false,
        }),
      )
    }
  }, [sections, currentIndex, dispatch, currentStep, currentStepEventName, resume?.id])

  const handleNext = useCallback(() => {
    if (!sections) return
    const nextStep = sections[currentIndex + 1]?.id
    if (shouldRedirectToAdditionalStep) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: SectionNames.addAdditionalSection,
            },
          },
          debounce: true,
        }),
      )
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    } else if (nextStep) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: nextStep,
            },
          },
          debounce: false,
        }),
      )
      if (currentStep) {
        trackInternalEvent('click_next_step', {
          label: 'step_builder',
          step: currentStepEventName,
          resume_id: resume?.id,
        })
      }
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
    } else {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: null,
            },
          },
          debounce: true,
        }),
      )
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: false }))
      dispatch(actions.setIsCustomizeModalOpen(true))
      trackInternalEvent('open_full_screen_resume_preview')
      trackInternalEvent('click_finish', {
        label: 'step_builder',
        resume_id: resume?.id,
        score: resumeScore,
      })
    }
  }, [
    sections,
    currentIndex,
    shouldRedirectToAdditionalStep,
    dispatch,
    currentStep,
    currentStepEventName,
    resume?.id,
    resumeScore,
  ])

  const currentSection = useMemo(() => {
    if (!sections) return null

    const section = sections[currentIndex]

    if (!section) {
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: SectionNames.addAdditionalSection,
            },
          },
          debounce: true,
        }),
      )
    } else {
      return section
    }
  }, [sections, currentIndex, dispatch])

  const nextButtonText = useMemo(() => {
    if (!sections) return
    if (shouldRedirectToAdditionalStep) {
      return i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.done_button_text`)
    } else if (currentStep === SectionNames.addAdditionalSection) {
      return i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.finish_button_text`)
    } else {
      return `${i18n.t(`${LOCALIZATION_PREFIX}.navigation_container.next_button_text`)}: ${
        sections[currentIndex + 1]?.title
      }`
    }
  }, [sections, shouldRedirectToAdditionalStep, currentStep, i18n, currentIndex])

  const isBackButtonPassive = useMemo(() => {
    return currentIndex === 0 && resumeScore >= BACK_BUTTON_CONDITION_WITH_SCORE_THRESHOLD
  }, [currentIndex, resumeScore])

  return {
    nextButtonText,
    currentSection,
    handleBack,
    handleNext,
    handleChangeStep,
    sections,
    currentIndex,
    isBackButtonPassive,
  }
}
